import type {
  CallHistoryItem,
  CallHistoryResponse,
  CallHistoryQueryParams,
  CallsByWorkspaceQueryParams,
  CallsByAgentManagerQueryParams,
  GetMissedCallsByUserResponse,
  GetCallByContactIdParams,
  CallRecordingPresignedUrlOutputDTO,
} from '@/types/call';
import { API_ENDPOINTS } from '@/constants';
import type { ApiBuilder } from './types';

/**
 * Build query string from pagination params
 */
const buildQueryString = (
  params: CallHistoryQueryParams,
  skipContact = false
): string => {
  const searchParams = new URLSearchParams();

  if (params.limit) {
    searchParams.append('limit', params.limit.toString());
  }
  if (params.cursor) {
    searchParams.append('cursor', params.cursor);
  }
  if (params.startDate) {
    searchParams.append('startDate', params.startDate);
  }
  if (params.endDate) {
    searchParams.append('endDate', params.endDate);
  }

  const queryString = searchParams.toString();
  return queryString ? `${skipContact ? '' : '?'}${queryString}` : '';
};

export const callHistoryApi = (builder: ApiBuilder) => ({
  /**
   * Get calls for the current authenticated agent
   * Uses JWT token automatically - no user ID parameter needed
   */
  getCallsByAgent: builder.query<CallHistoryResponse, CallHistoryQueryParams>({
    query: params => ({
      url: `${API_ENDPOINTS.CALL.BY_AGENT}${buildQueryString(params)}`,
      method: 'GET',
    }),
    providesTags: ['Call'],
  }),

  /**
   * Get calls by workspace ID (same pagination params as by-user).
   */
  getCallsByWorkspace: builder.query<
    CallHistoryResponse,
    CallsByWorkspaceQueryParams
  >({
    query: ({ workspaceId, ...params }) => ({
      url: `${API_ENDPOINTS.CALL.BY_WORKSPACE}/${encodeURIComponent(workspaceId)}${buildQueryString(params)}`,
      method: 'GET',
    }),
    providesTags: ['Call'],
  }),

  /**
   * Get missed calls for the current authenticated user
   */
  getMissedCallsByUser: builder.query<
    GetMissedCallsByUserResponse,
    CallHistoryQueryParams
  >({
    query: params => ({
      url: `${API_ENDPOINTS.CALL.MISSED_BY_USER}${buildQueryString(params)}`,
      method: 'GET',
    }),
    providesTags: ['Call'],
  }),

  /**
   * Get calls for a specific agent (manager view)
   * Requires manager role
   * Returns calls for the specified agent ID with cursor-based pagination
   */
  getCallsByAgentManager: builder.query<
    CallHistoryResponse,
    CallsByAgentManagerQueryParams
  >({
    query: ({ agentIds, ...params }) => ({
      url: `${API_ENDPOINTS.CALL.BY_AGENT_MANAGER}?${agentIds
        .map(id => `agentIds=${encodeURIComponent(id)}`)
        .join('&')}&${buildQueryString(params, true)}`,
      method: 'GET',
    }),
    keepUnusedDataFor: 0,
    providesTags: ['Call'],
  }),

  /**
   * Get a single call by contact ID
   */
  getCallByContactId: builder.query<CallHistoryItem, GetCallByContactIdParams>({
    query: ({ contactId }) => ({
      url: `${API_ENDPOINTS.CALL.BY_CONTACT}/${encodeURIComponent(contactId)}`,
      method: 'GET',
    }),
    providesTags: ['Call'],
  }),

  /**
   * Get presigned URL for a call recording by contact ID.
   */
  getCallRecordingPresignedUrl: builder.query<
    CallRecordingPresignedUrlOutputDTO,
    string
  >({
    query: contactId => ({
      url: `${API_ENDPOINTS.CALL.RECORDING}/${encodeURIComponent(contactId)}/recording`,
      method: 'GET',
    }),
  }),
});
